/**
 * The consolidation drill — one door in one consolidation run, `/exit/consolidation/:storeKey`
 * (bata-ui#148 step 4.4, `docs/adr/7002`).
 *
 * The door is a PATH segment and the run stays `?runId=` (`CONSOLIDATION_RUN_ID_PARAM`), so the
 * page is a link a planner can send. Pure, so the screen, its export and the consolidation
 * screen's door links share one spelling and the tests assert on strings.
 */

import type { ConsolidationLineSummary } from '@/api/generated';

import { CONSOLIDATION_OPTION_PARAM, CONSOLIDATION_RUN_ID_PARAM } from './scope';

/**
 * The consolidation pages — Estate · Doors · Lanes · Lane what-if, rail sub-entries of Clearance
 * consolidation (client rules 1 and 3, DEC-84, DEC-88; `docs/adr/7001`, amendment of
 * 2026-09-30). One screen used to hold all four, three of them gated by `?optionId=`.
 *
 * **Sibling paths, never `/exit/consolidation/doors`.** Every second segment under
 * `/exit/consolidation/` is a door (`DOOR_DRILL_PATTERN`): a store keyed `doors` or `lanes`
 * would otherwise be unreachable, and the rail's longest-prefix rule would mark the door
 * drill as a sub-page. `door-drill.test.ts` asserts the router resolves each sibling to its
 * own route and the drill still captures a door.
 */
export const CONSOLIDATION_PATH = '/exit/consolidation';
export const CONSOLIDATION_DOORS_PATH = '/exit/consolidation-doors';
export const CONSOLIDATION_LANES_PATH = '/exit/consolidation-lanes';
export const LANE_WHATIF_PATH = '/exit/lane-whatif';
/**
 * The run-wide lists (ADR 9069): every door position / every lane of a run, with no option chosen — the pages a
 * card that counts the whole run opens. Sibling paths too, for the reason above.
 */
export const CONSOLIDATION_RUN_DOORS_PATH = '/exit/consolidation-run-doors';
export const CONSOLIDATION_RUN_LANES_PATH = '/exit/consolidation-run-lanes';

/** A consolidation page, scoped to the run and, when given, one option. */
export function consolidationHref(path: string, runId: string | null, optionId?: string): string {
  const query = new URLSearchParams({
    ...(runId === null ? {} : { [CONSOLIDATION_RUN_ID_PARAM]: runId }),
    ...(optionId === undefined ? {} : { [CONSOLIDATION_OPTION_PARAM]: optionId }),
  }).toString();
  return query === '' ? path : `${path}?${query}`;
}
/** The manifest's declared drill target, verbatim. */
export const DOOR_DRILL_PATTERN = '/exit/consolidation/:storeKey';

/** The door a pathname names, or `null`. A blank or undecodable segment is not a door. */
export function readDoorDrill(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  const base = CONSOLIDATION_PATH.split('/').filter(Boolean);
  if (!base.every((segment, index) => segments[index] === segment)) return null;
  if (segments.length !== base.length + 1) return null;
  try {
    const value = decodeURIComponent(segments[base.length] ?? '').trim();
    return value === '' ? null : value;
  } catch {
    return null;
  }
}

export function doorPath(storeKey: string): string {
  return `${CONSOLIDATION_PATH}/${encodeURIComponent(storeKey)}`;
}

export function doorHref(storeKey: string, runId: string | null): string {
  const query = runId === null ? '' : new URLSearchParams({ [CONSOLIDATION_RUN_ID_PARAM]: runId }).toString();
  return query === '' ? doorPath(storeKey) : `${doorPath(storeKey)}?${query}`;
}

/** A door's lane book, split by direction — what leaves it and what arrives. */
export interface DoorLaneFigures {
  readonly lanes: number;
  readonly proposedLanes: number;
  readonly proposedUnits: number;
}

export function doorLaneFigures(lines: readonly ConsolidationLineSummary[]): DoorLaneFigures {
  const proposed = lines.filter((line) => line.is_proposed);
  return {
    lanes: lines.length,
    proposedLanes: proposed.length,
    proposedUnits: proposed.reduce((sum, line) => sum + line.units, 0),
  };
}
